// =======================================================
// Login.jsx — FULL UPDATED VERSION (GoogleAuthButton REMOVED)
// =======================================================

import { useState, useEffect, useRef } from "react";
import {
  TextField,
  Button,
  Link as MLink,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Link, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";

import Navbar from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import api, { warmUpBackend } from "../utils/api";

import ResendVerifyModal from "../components/ResendVerifyModal";
import PageContainer from "../components/PageContainer";
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // ---------------------------
  // FORM STATE
  // ---------------------------
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---------------------------
  // ERROR STATE
  // ---------------------------
  const [credentialError, setCredentialError] = useState("");
  const [generalError, setGeneralError] = useState("");

  // ---------------------------
  // MODALS
  // ---------------------------
  const [unverifiedModalOpen, setUnverifiedModalOpen] = useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);

  // ---------------------------
  // RESEND COOLDOWN (KEPT)
  // ---------------------------
  const [cooldown, setCooldown] = useState(0);
  const resendRef = useRef(null);

  // Wake backend
  useEffect(() => {
    warmUpBackend();
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => setCooldown((p) => p - 1), 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // ---------------------------
  // VALIDATION
  // ---------------------------
  const validate = () => {
    if (!email) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return "Enter a valid email address.";
    if (!password) return "Password is required.";
    if (password.length < 6)
      return "Password must be at least 6 characters.";
    return "";
  };

  // ---------------------------
  // ROLE REDIRECT
  // ---------------------------
  const getRedirectPath = (role) => {
    if (role === "admin") return "/admin";
    if (role === "agent") return "/agent/dashboard";
    return "/dashboard";
  };

  // ---------------------------
  // OPEN RESEND FROM UNVERIFIED MODAL
  // ---------------------------
  const openResendFromUnverifiedModal = () => {
    setUnverifiedModalOpen(false);
    setResendModalOpen(true);
    setCooldown(60);
  };

  // ---------------------------
  // SUBMIT LOGIN
  // ---------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset UI states
    setCredentialError("");
    setGeneralError("");
    setUnverifiedModalOpen(false);

    const v = validate();
    if (v) {
      setCredentialError(v);
      return;
    }

    setLoading(true);

    try {
      const res = await api.post("/auth/login", { email, password });
      const data = res.data;

      login(data.user, data.token, data.sessionToken);
      navigate(getRedirectPath(data.user.role));
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;

      // ❌ Incorrect email or password → inline only
      if (status === 401) {
        setCredentialError("Incorrect email or password");
        return;
      }

      // ⚠️ Unverified email → modal
      if (status === 403 && msg === "Email not verified") {
        setUnverifiedModalOpen(true);
        return;
      }

      // 🚫 Other errors (suspended / server)
      setGeneralError(msg || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
    <>
      <Navbar />

      {/* ===================== MODAL #1 — UNVERIFIED NOTICE ===================== */}
      <Dialog
        open={unverifiedModalOpen}
        onClose={() => setUnverifiedModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Verify your email
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
            Your account is not verified yet.
            <br />
            Please check your <b>Inbox</b> or <b>Spam</b> folder to verify your
            account.
          </Typography>

          <Box sx={{ mt: 2, p: 1.4, borderRadius: 2, bgcolor: "#fff8e1" }}>
            <Typography sx={{ fontSize: 13 }}>
              Email: <b>{email || "—"}</b>
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: "#555", mt: 0.5 }}>
              Didn’t receive the email? You can resend the verification notification.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button type="button" variant="outlined" onClick={() => setUnverifiedModalOpen(false)}>
            Close
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={openResendFromUnverifiedModal}
            disabled={!email}
          >
            Resend Notification
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================== MODAL #2 — RESEND ===================== */}
      <ResendVerifyModal
        open={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        defaultEmail={email}
      />

      {/* ===================== PAGE ===================== */}
      <Box
        sx={{
          minHeight: "85vh",
          background: "#f4f6fb",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
          alignItems: "stretch",
        }}
      >
        {/* ================= LEFT SIDE ================= */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: { xs: 2, md: 6 },
            py: { xs: 4, md: 0 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 420,
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "white",
            }}
          >
            <Typography
              variant="h5"
              fontWeight={800}
              textAlign="center"
              mb={1.5}
            >
              Login to Your Account
            </Typography>

            <Typography
              textAlign="center"
              sx={{ mb: 2.5, color: "text.secondary", fontSize: 14 }}
            >
              Continue your CBT practice and track your progress.
            </Typography>

            {/* FORM */}
            <form onSubmit={handleSubmit} noValidate>
              <TextField
                label="Email Address"
                type="email"
                fullWidth
                required
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ mt: 3, py: 1.3, borderRadius: 2, fontSize: 16 }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "white" }} />
                ) : (
                  "Login"
                )}
              </Button>

              {credentialError && (
                <Typography
                  sx={{ mt: 1, fontSize: 13, color: "error.main", textAlign: "center" }}
                >
                  {credentialError}
                </Typography>
              )}

              {generalError && (
                <Typography
                  sx={{ mt: 1, fontSize: 13, color: "error.main", textAlign: "center" }}
                >
                  {generalError}
                </Typography>
              )}
            </form>

            {/* MOBILE RESEND (KEPT) */}
            <Box
              ref={resendRef}
              sx={{
                mt: 2,
                display: { xs: "block", md: "none" },
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                Didn’t receive the verification email?
              </Typography>

              <Button
                type="button"
                variant="outlined"
                size="small"
                disabled={!email || cooldown > 0}
                onClick={() => {
                  setResendModalOpen(true);
                  setCooldown(60);
                }}
                sx={{ borderRadius: 2, px: 2.5 }}
              >
                {cooldown > 0
                  ? `Resend available in ${Math.floor(cooldown / 60)}:${String(
                      cooldown % 60
                    ).padStart(2, "0")}`
                  : "Resend Verification Email"}
              </Button>
            </Box>

            {/* LINKS */}
            <MLink component={Link} to="/forgot-password" underline="hover"
              sx={{ mt: 2, display: "block", textAlign: "center", fontSize: 14 }}>
              Forgot password?
            </MLink>

            <MLink component={Link} to="/register" underline="hover"
              sx={{ mt: 1, display: "block", textAlign: "center", fontSize: 14 }}>
              Don’t have an account? Register
            </MLink>
          </Paper>
        </Box>

        {/* ================= RIGHT SIDE ================= */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            px: 6,
            py: 4,
            borderLeft: "1px solid #e0e0e0",
            background:
              "linear-gradient(135deg, rgba(13,110,253,0.08), rgba(13,110,253,0.02))",
          }}
        >
          <Typography variant="h4" fontWeight={900} mb={2}>
            Having Trouble Logging In?
          </Typography>

          <Typography sx={{ mb: 2, fontSize: 15, lineHeight: 1.8 }}>
            • Ensure your email and password are correct.
            <br />• Your email must be <b>verified</b> before access.
          </Typography>

          <Typography sx={{ mb: 2, fontSize: 15, lineHeight: 1.8 }}>
            Didn’t receive the verification mail?
            <br />
            Check your inbox or spam folder.
            <br />
            Verification link can only be resent after 10 minutes.
          </Typography>

          <Button
            type="button"
            variant="outlined"
            sx={{ mt: 1.5, maxWidth: 260 }}
            onClick={() => setResendModalOpen(true)}
          >
            Resend Verification Email
          </Button>
        </Box>
      </Box>

      <Footer />
    </>
		  </PageContainer>
  );
}
